/**
 * FreePhotoVectorTileController
 *
 * A renderless component that adds FreePhoto vector tile layer to the global MapLibre map.
 * Replaces the GeoJSON cluster approach (FreePhotoMapController) with server-side MVT for
 * better performance with large datasets (1000+ photos).
 *
 * Source layer: 'free_photo'
 * Tile endpoint: GET /api/geocollect/tiles/free-photo/{z}/{x}/{y}.pbf
 */
import { memo, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { isMapLoadedDoneAtom, showFreePhotoVectorTilesAtom } from '../stores/mapStore';
import FreePhotoVectorTileUtils from '../utils/FreePhotoVectorTileUtils';

// Legacy source/layer IDs from the old GeoJSON cluster controller (FreePhotoMapController.tsx)
const LEGACY_SOURCE_ID = 'freephoto-points';
const LEGACY_LAYERS = ['freephoto-clusters', 'freephoto-cluster-count'];

const cleanupLegacyLayers = () => {
  const map = window.map;
  if (!map) return;
  LEGACY_LAYERS.forEach((id) => { if (map.getLayer(id)) map.removeLayer(id); });
  if (map.getSource(LEGACY_SOURCE_ID)) map.removeSource(LEGACY_SOURCE_ID);
};

const FreePhotoVectorTileController = () => {
  const currentProject = useAtomValue(currentProjectAtom);
  const isMapLoadedDone = useAtomValue(isMapLoadedDoneAtom);
  const showFreePhotoTiles = useAtomValue(showFreePhotoVectorTilesAtom);

  // Mount / unmount / project change
  useEffect(() => {
    if (!isMapLoadedDone) return;

    // Remove any leftover layers from the old GeoJSON cluster approach
    cleanupLegacyLayers();

    FreePhotoVectorTileUtils.addToMap(currentProject?.id);
    FreePhotoVectorTileUtils.moveLayerToTop();

    return () => {
      FreePhotoVectorTileUtils.removeFromMap();
    };
  }, [isMapLoadedDone, currentProject?.id]);

  // Toggle visibility
  useEffect(() => {
    if (!isMapLoadedDone) return;
    FreePhotoVectorTileUtils.setVisible(showFreePhotoTiles);
  }, [isMapLoadedDone, showFreePhotoTiles]);

  // Re-add and move to top when basemap changes
  useEffect(() => {
    const handleBasemapChanged = () => {
      if (showFreePhotoTiles) {
        FreePhotoVectorTileUtils.addToMap(currentProject?.id);
        FreePhotoVectorTileUtils.moveLayerToTop();
      }
    };

    window.addEventListener('basemapChanged', handleBasemapChanged);
    return () => {
      window.removeEventListener('basemapChanged', handleBasemapChanged);
    };
  }, [currentProject?.id, showFreePhotoTiles]);

  return null;
};

export default memo(FreePhotoVectorTileController);
